import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import adminPricingService from "./adminPricingService";

const SubscriptionFormModal = ({ initialData, onClose, onSuccess }) => {
  const [name, setName] = useState(initialData?.name || "");
  const [description, setDescription] = useState(
    initialData?.description || ""
  );
  const [price, setPrice] = useState(initialData?.price || "");
  const [discountPercentage, setDiscountPercentage] = useState(
    initialData?.discount_percentage || ""
  );
  const [features, setFeatures] = useState(
    (initialData?.features || []).map((f) => ({
      feature_id: f.feature.id,
      included: f.included,
      value: f.value || "",
      unit: f.unit || "",
      custom_label: f.custom_label || "",
      feature: f.feature,
    }))
  );

  const toggleIncluded = (featureId) => {
    setFeatures((prev) =>
      prev.map((f) =>
        f.feature.id === featureId ? { ...f, included: !f.included } : f
      )
    );
  };

  const handleSubmit = async () => {
    const basePayload = {
      name,
      description,
      price: parseFloat(price),
      discount_percentage: discountPercentage
        ? parseFloat(discountPercentage)
        : "0.00",
    };

    const featuresPayload = {
      features: features.map((f) => {
        const payload = {
          feature_id: f.feature_id,
          included: f.included,
        };

        if (f.included) {
          if (f.value?.trim()) payload.value = f.value.trim();
          if (f.unit?.trim()) payload.unit = f.unit.trim();
          if (f.custom_label?.trim())
            payload.custom_label = f.custom_label.trim();
        }

        return payload;
      }),
    };

    try {
      await adminPricingService.updatePackage(initialData.id, basePayload);
      await adminPricingService.updatePackageFeatures(
        initialData.id,
        featuresPayload
      );

      toast.success("Package updated successfully!");
      onSuccess();
      onClose();
    } catch (err) {
      console.error("Update failed:", err);
      toast.error("Failed to update package.");
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-lg shadow-lg relative">
        <style>{`
          input[type="checkbox"].switch {
            appearance: none;
            width: 2.5rem;
            height: 1.4rem;
            background: #d1d5db;
            border-radius: 9999px;
            position: relative;
            transition: background 0.3s ease;
          }
          input[type="checkbox"].switch:checked {
            background: #3b82f6;
          }
          input[type="checkbox"].switch::before {
            content: "";
            position: absolute;
            top: 2px;
            left: 2px;
            width: 1rem;
            height: 1rem;
            background: white;
            border-radius: 9999px;
            transition: transform 0.3s ease;
          }
          input[type="checkbox"].switch:checked::before {
            transform: translateX(1.1rem);
          }
        `}</style>

        <h2 className="text-xl font-bold mb-4">Edit Subscription</h2>

        <div className="mb-4">
          <label htmlFor="name" className="block text-sm font-medium mb-1">
            Package Name
          </label>
          <input
            id="name"
            className="w-full p-2 border rounded"
            type="text"
            placeholder="Package Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>

        <div className="mb-4">
          <label
            htmlFor="description"
            className="block text-sm font-medium mb-1"
          >
            Description
          </label>
          <textarea
            id="description"
            className="w-full p-2 border rounded"
            placeholder="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        <div className="flex gap-4 mb-4">
          <div className="flex-1">
            <label htmlFor="price" className="block text-sm font-medium mb-1">
              Price($)
            </label>
            <input
              id="price"
              className="w-full p-2 border rounded"
              type="number"
              placeholder="Price"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
            />
          </div>
          <div className="flex-1">
            <label
              htmlFor="discount"
              className="block text-sm font-medium mb-1"
            >
              Discount (%)
            </label>
            <input
              id="discount"
              className="w-full p-2 border rounded"
              type="number"
              placeholder="Discount Percentage"
              value={discountPercentage}
              onChange={(e) => setDiscountPercentage(e.target.value)}
            />
          </div>
        </div>

        <div className="mb-4">
          <h4 className="font-medium mb-2">Select Features</h4>
          <div className="space-y-4 max-h-64 overflow-y-auto pr-2">
            {features.map((f, index) => (
              <div key={f.feature.id} className="border-b pb-4 mb-4">
                {/* Top row: name + toggle */}
                <div className="flex justify-between items-start">
                  <div className="flex-1 pr-4">
                    <p
                      className={`text-sm font-medium ${
                        f.included ? "text-black" : "line-through text-gray-400"
                      }`}
                    >
                      {f.feature.name}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {f.feature.description}
                    </p>
                  </div>

                  <input
                    type="checkbox"
                    className="switch mt-1"
                    checked={f.included}
                    onChange={() =>
                      setFeatures((prev) =>
                        prev.map((item, i) =>
                          i === index
                            ? { ...item, included: !item.included }
                            : item
                        )
                      )
                    }
                  />
                </div>

                {/* Optional fields if included */}
                {f.included && (
                  <div className="mt-2 grid grid-cols-1 gap-2">
                    <input
                      type="text"
                      placeholder="Value"
                      className="p-2 border rounded text-sm"
                      value={f.value}
                      onChange={(e) =>
                        setFeatures((prev) =>
                          prev.map((item, i) =>
                            i === index
                              ? { ...item, value: e.target.value }
                              : item
                          )
                        )
                      }
                    />
                    {/* <input
                      type="text"
                      placeholder="Custom Label"
                      disabled
                      className="p-2 border rounded text-sm"
                      value={f.custom_label}
                      onChange={(e) =>
                        setFeatures((prev) =>
                          prev.map((item, i) =>
                            i === index
                              ? { ...item, custom_label: e.target.value }
                              : item
                          )
                        )
                      }
                    /> */}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-end space-x-2 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Submit
          </button>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionFormModal;
